import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import { Button, Icon, Text, FormControl } from '@chakra-ui/react'
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineTag } from 'react-icons/ai'
import useFetch, { CachePolicies } from 'use-http'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import { IKReactSelectCreatable } from '../../../../ui-kit'
import useToast from '../../../hooks/useToast'
import type {
  BulkTagsErrorResponse,
  BulkTagsSuccessResponse,
  ClientTagRequestResponse,
  Option,
  UpdateTagsErrorResponse,
  UpdateTagsSuccessResponse,
} from '../../../interfaces'
import { MLContext, UserContext } from '../../../providers'
import { MediaLibraryActions } from '../../../providers/MLProvider'
import { ClientTagResponse } from '../../../interfaces'
import {
  DEFAULT_CLIENT_TAG_LIMIT,
  DEFAULT_CLIENT_TAG_SKIP,
} from '../../../constants'
import { useDebounce } from '../../../hooks/useDebounce'
import { formatTags } from '../../../../utils/mediaLibrary'

interface Props {
  operation: 'addTagsBulk' | 'removeTagsBulk' | 'updateTags'
  isOpen: boolean
  onClose(): void
}

interface FormProps {
  tags: Array<{
    label: string
    value: string
  }>
}


const Tags = ({ operation, isOpen, onClose }: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()
  const [tags, setTags] = useState<string[]>([])
  const [tagOptions, setTagOptions] = useState<Option[]>([])
  const [options, setOptions] = useState<Option[]>([])
  const [{ selectedFiles }, dispatch] = useContext(MLContext)

  const [suggestedTags, setSuggestedTags] = useState<ClientTagResponse[]>([])
  const [search, setSearch] = useState<string>()
  const debouncedSearchTerm = useDebounce(search, 500)

  const { response, request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const { request: tagRequest } = useFetch<ClientTagRequestResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const createOption = (label: string): Option => ({
    label,
    value: label.toLowerCase(),
  })
  useEffect(() => {
    if (operation === 'updateTags' && isOpen) {
      const existingTags = selectedFiles[0]?.tags
      setTags(existingTags ?? [])

      const tagOption = selectedFiles[0]?.tags?.map(tag => createOption(tag))
      setTagOptions(tagOption ?? [])
      setOptions(tagOption ?? [])
    }
  }, [isOpen])

  const operationText =
    operation === 'addTagsBulk'
      ? t('add_tags_on', 'Add Tags on')
      : operation === 'removeTagsBulk'
      ? t('remove_tags_from', 'Remove Tags from')
      : t('edit_tags_on', 'Edit Tags on')


  
  const getSuggestedTags = useCallback(async (debouncedSearchTerm) => {
    if (user?.clientNumber && isOpen) {
      let url = `/api/clients/${user?.clientNumber}/client-tags?limit=${DEFAULT_CLIENT_TAG_LIMIT}&skip=${DEFAULT_CLIENT_TAG_SKIP}`
      if (debouncedSearchTerm) url = `${url}&label=${debouncedSearchTerm}`

      const response = await tagRequest.get(url)

      if (tagRequest.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else if (response?.tagsData && Array.isArray(response.tagsData)) {
        setSuggestedTags(response.tagsData)
      }
    }
  }, [user, isOpen])

  useEffect(() => {
    getSuggestedTags(debouncedSearchTerm)
  }, [debouncedSearchTerm, user, isOpen])

  const tagsOptions = useMemo(() => {
    return suggestedTags.map(data => ({
      label: data.label,
      value: data.name,
    }))
  }, [suggestedTags])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setTags([])
        setTagOptions([])
      }}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ tags: tagOptions }}
          onSubmit={async values => {
            if (operation === 'updateTags') {
              const fileId = selectedFiles[0]?.fileId
              const res = (await request.patch(
                `/api/v2/files/${fileId}/details`,
                {
                  tags: tags.length > 0 ? tags : null,
                },
              )) as UpdateTagsSuccessResponse | UpdateTagsErrorResponse

              if (request.error) {
                if ([400, 404].includes(response.status)) {
                  toast({
                    description: (res as UpdateTagsErrorResponse).message,
                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                } else {
                  toast({
                    description: t(
                      'error_in_updating_tags',
                      'Error in updating tags, please try again after sometime',
                    ),
                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                }
              } else {
                toast({
                  description: t(
                    'tags_updated_successfully',
                    'Tags updated successfully',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })

                const fileDetail = selectedFiles[0]
                if (fileDetail) {
                  const updated = {
                    ...fileDetail,
                    tags: tags.length > 0 ? [...tags] : [],
                  }
                  dispatch({
                    type: MediaLibraryActions.UPDATE_FILES,
                    payload: [updated],
                  })
                  dispatch({
                    type: MediaLibraryActions.SET_SELECTED_FILES,
                    payload: [updated],
                  })
                }
              }
            } else {
              const fileIds = selectedFiles.map(file => file.fileId)
              const res = (
                operation === 'addTagsBulk'
                  ? await request.post('/api/v2/files/addTags', {
                      fileIds,
                      tags,
                    })
                  : await request.post('/api/v2/files/removeTags', {
                      fileIds,
                      tags,
                    })
              ) as BulkTagsSuccessResponse | BulkTagsErrorResponse

              if (request.error) {
                if ([400, 404].includes(response.status)) {
                  toast({
                    description: (res as BulkTagsErrorResponse).message,
                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                } else {
                  toast({
                    description:
                      operation === 'addTagsBulk'
                        ? t(
                            'error_in_adding_tags',
                            'Error in adding tags, please try again after sometime',
                          )
                        : t(
                            'error_in_removing_tags',
                            'Error in removing tags, please try again after sometime',
                          ),
                    status: 'error',
                    isClosable: true,
                    duration: 5000,
                    position: 'top-right',
                    variant: 'outline',
                  })
                }
              } else if (response.status === 207) {
                toast({
                  description: (res as BulkTagsSuccessResponse).message,
                  status: 'info',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              } else {
                toast({
                  description:
                    operation === 'addTagsBulk'
                      ? t('tags_added_successfully', 'Tags added successfully')
                      : t(
                          'tags_removed_successfully',
                          'Tags removed successfully',
                        ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })

                const patched = selectedFiles.map(file => {
                  const current = Array.isArray(file.tags) ? file.tags : []
                  let nextTags: string[]
                  if (operation === 'addTagsBulk') {
                    nextTags = Array.from(new Set([...current, ...tags]))
                  } else {
                    const toRemove = new Set(tags)
                    nextTags = current.filter(tag => !toRemove.has(tag))
                  }
                  return { ...file, tags: nextTags }
                })
                if (patched.length > 0) {
                  dispatch({
                    type: MediaLibraryActions.UPDATE_FILES,
                    payload: patched,
                  })
                  dispatch({
                    type: MediaLibraryActions.SET_SELECTED_FILES,
                    payload: patched,
                  })
                }
              }
            }
            onClose()
            setTags([])
            setTagOptions([])
            setOptions([])
          }}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <ModalHeader>
                <Icon
                  as={AiOutlineTag}
                  color="appBattleshipGrey"
                  backgroundColor="appPaleGrey"
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="50%"
                  marginRight="0.5em"
                />
                {operation === 'addTagsBulk'
                  ? t('add_bulk_tags', 'Add Bulk Tags')
                  : operation === 'removeTagsBulk'
                  ? t('remove_bulk_tags', 'Remove Bulk Tags')
                  : t('tags', 'Tags')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Field name="tags">
                  {({ field, form }: FieldProps<string, FormProps>) => {
                    const handleChange = (options: Option[]): void => {

                      const newValue = formatTags(options)

                      const newTags = newValue.map(option => option.label)
                      setTags(newTags)

                      const newTagOptions = newValue.map(option =>
                        createOption(option.label),
                      )
                      setTagOptions(newTagOptions)
                      form.setFieldValue(field.name, newValue)
                    }

                    return (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.tags && form.touched.tags,
                        )}
                        isRequired
                      >
                        <Text mb={4}>
                          <Trans t={t} i18nKey="tags_modal_description">
                            {{ operationText }} selected file(s)
                          </Trans>
                        </Text>
                        <IKReactSelectCreatable
                          {...field}
                          isMulti
                          defaultValue={options}
                          options={tagsOptions}
                          inputValue={search}
                          onInputChange={value => setSearch(value)}
                          onChange={handleChange}
                          value={tagOptions}
                          isClearable={false}
                          skipExactMatch={true}
                        />
                      </FormControl>
                    )
                  }}
                </Field>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={!dirty}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default Tags
